'use client';

import {
  FaBars,
  FaChevronLeft,
  FaChevronRight,
  FaComments,
  FaGear,
  FaShieldHalved,
  FaTriangleExclamation,
  FaUser,
} from 'react-icons/fa6';
import { SignInButton, UserButton } from '@clerk/nextjs';
import DeleteAccount from '@/app/settings/DeleteAccount';
import { useClerk, useUser } from '@clerk/nextjs';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { usePathname, useRouter } from 'next/navigation';

import { FiCalendar } from 'react-icons/fi';
import {
  addWeeks,
  endOfDay,
  endOfWeek,
  format,
  isValid,
  parse,
  startOfMonth,
  startOfDay,
  startOfWeek,
} from 'date-fns';
import Calendar from 'react-calendar';
import ChatModal from '@/app/chat/ChatModal';
import { CoachButton } from './CoachButton';
import Image from 'next/image';
import Link from 'next/link';
import { ToastContainer } from 'react-toastify';
import { cn } from '@/utils/uiUtils';
import { trpc } from '@/app/api/trpc/client';
import { useSettingsStore } from '@/app/stores/settingsStore';
import { useTheme } from '@/app/components/Theme';

type NavItem = {
  id: string;
  href: string;
  onClick?: () => void;
  icon: React.ReactNode;
  label: string;
  public: boolean;
};

const ADMIN_NAV_ITEM: NavItem = {
  id: 'admin',
  href: '/admin',
  icon: <FaShieldHalved className='icon' />,
  label: 'Admin',
  public: false,
};

const ADMIN_ROLES = new Set(['admin', 'org:admin', 'org:owner']);
const CALENDAR_VISIBLE_MONTH_EVENT = 'calendar:visible-month-change';
const CALENDAR_SCROLL_TO_DATE_EVENT = 'calendar:scroll-to-date';

const navItems: NavItem[] = [
  {
    id: 'calendar',
    href: '/calendar',
    icon: <FiCalendar className='icon' />,
    label: 'Calendar',
    public: false,
  },
  {
    id: 'forum',
    href: '/forum',
    icon: <FaComments className='icon' />,
    label: 'Forum',
    public: false,
  },
  {
    id: 'settings',
    href: '/settings',
    icon: <FaGear className='icon' />,
    label: 'Settings',
    public: false,
  },
];

const Header = () => {
  const { isSignedIn, user } = useUser();
  const { signOut } = useClerk();
  const { isDarkMode } = useTheme();
  const setTrainingSettings = useSettingsStore(
    (state) => state.setTrainingSettings
  );
  const router = useRouter();
  const pathname = usePathname();
  const utils = trpc.useUtils();
  const calendarPrefetchRange = useMemo(() => {
    const weekStart = startOfWeek(new Date(), { weekStartsOn: 1 });
    const nextWeekEnd = endOfWeek(addWeeks(weekStart, 1), {
      weekStartsOn: 1,
    });
    return {
      start: startOfDay(weekStart),
      end: endOfDay(nextWeekEnd),
    };
  }, []);
  const [isOrgAdmin, setIsOrgAdmin] = useState(false);

  // Load settings when user is signed in
  const { data: trainingSettings } = trpc.settings.getTrainingSettings.useQuery(
    undefined,
    {
      enabled: isSignedIn ?? false,
      staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    }
  );

  // Sync settings to store when they load
  useEffect(() => {
    if (trainingSettings) {
      setTrainingSettings(trainingSettings);
    }
  }, [trainingSettings, setTrainingSettings]);

  const prefetchSettingsData = useCallback(() => {
    utils.settings.getTrainingSettings.prefetch();
    utils.settings.getTrainingZones.prefetch();
    utils.settings.getCoaches.prefetch();
    utils.planInstance.getActive.prefetch();
    utils.settings.getRaceTarget.prefetch();
  }, [utils]);

  const prefetchCalendarData = useCallback(() => {
    const { start, end } = calendarPrefetchRange;
    const dateFilter = {
      filter: {
        date: {
          gte: start,
          lt: end,
        },
      },
    };

    utils.activities.getMany.prefetch({
      filter: {
        startDate: {
          gte: start,
          lt: end,
        },
      },
    });
    utils.workouts.getMany.prefetch(dateFilter);
    utils.analysis.getMany.prefetch(dateFilter);
    utils.analysis.getTimelyAnalysisRange.prefetch({
      startDate: start,
      endDate: end,
    });
  }, [calendarPrefetchRange, utils]);

  useEffect(() => {
    if (!pathname) return;
    if (pathname.startsWith('/calendar')) {
      router.prefetch('/settings');
      prefetchSettingsData();
    }
    if (pathname.startsWith('/settings')) {
      router.prefetch('/calendar');
      prefetchCalendarData();
    }
  }, [pathname, router, prefetchSettingsData, prefetchCalendarData]);

  const handleNavMouseEnter = useCallback(
    (item: NavItem) => {
      router.prefetch(item.href);
      if (item.href === '/settings') {
        prefetchSettingsData();
      } else if (item.href === '/calendar') {
        prefetchCalendarData();
      }
    },
    [router, prefetchSettingsData, prefetchCalendarData]
  );

  useEffect(() => {
    if (!isSignedIn) {
      const timeout = setTimeout(() => setIsOrgAdmin(false), 0);
      return () => {
        clearTimeout(timeout);
      };
    }

    let active = true;
    fetch('/api/admin/whoami', { cache: 'no-store' })
      .then((res) => res.json())
      .then((data) => {
        if (!active) return;
        const resolvedRole =
          data.orgRole ??
          data.role ??
          data.orgRoleClaim ??
          data.orgRoleAlt ??
          null;
        setIsOrgAdmin(ADMIN_ROLES.has(resolvedRole ?? ''));
      })
      .catch(() => {
        if (!active) return;
        setIsOrgAdmin(false);
      });

    return () => {
      active = false;
    };
  }, [isSignedIn]);

  const gitCommit = process.env.NEXT_PUBLIC_GIT_COMMIT ?? 'TrainMe';
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [visibleCalendarMonth, setVisibleCalendarMonth] = useState<string>();
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [datePickerMonth, setDatePickerMonth] = useState(new Date());
  const datePickerRef = useRef<HTMLDivElement>(null);
  const availableNavItems = isOrgAdmin
    ? [ADMIN_NAV_ITEM, ...navItems]
    : navItems;
  const handleSignOut = useCallback(() => {
    setIsOrgAdmin(false);
    signOut();
  }, [signOut]);
  const showCalendarTodayButton = pathname?.startsWith('/calendar') ?? false;

  useEffect(() => {
    if (!showCalendarTodayButton) {
      setVisibleCalendarMonth(undefined);
      return;
    }

    const handleVisibleMonthChange = (event: Event) => {
      const month = (event as CustomEvent<{ month?: string }>).detail?.month;
      if (!month) return;

      setVisibleCalendarMonth(month);
      const parsedMonth = parse(month, 'MMMM yyyy', new Date());
      if (isValid(parsedMonth)) setDatePickerMonth(parsedMonth);
    };

    window.addEventListener(
      CALENDAR_VISIBLE_MONTH_EVENT,
      handleVisibleMonthChange
    );
    return () =>
      window.removeEventListener(
        CALENDAR_VISIBLE_MONTH_EVENT,
        handleVisibleMonthChange
      );
  }, [showCalendarTodayButton]);

  useEffect(() => {
    if (!isDatePickerOpen) return;

    const handlePointerDown = (event: PointerEvent) => {
      if (!datePickerRef.current?.contains(event.target as Node)) {
        setIsDatePickerOpen(false);
      }
    };

    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [isDatePickerOpen]);

  const handleTodayClick = useCallback(() => {
    window.dispatchEvent(new Event('calendar:scroll-to-today'));
  }, []);
  const handleCalendarDateSelect = useCallback((date: Date) => {
    setIsDatePickerOpen(false);
    window.dispatchEvent(
      new CustomEvent(CALENDAR_SCROLL_TO_DATE_EVENT, {
        detail: { date: format(date, 'yyyy-MM-dd') },
      })
    );
  }, []);
  const handleCalendarNavigate = useCallback((direction: 'up' | 'down') => {
    window.dispatchEvent(
      new CustomEvent('calendar:navigate', { detail: { direction } })
    );
  }, []);

  return (
    <header className='flex justify-between items-center fixed h-12 px-4 bg-gray-400 dark:bg-gray-900 text-gray-900 dark:text-gray-100 p-1 top-0 left-0 right-0 z-20 shadow-md'>
      <FaBars
        className='icon cursor-pointer block md:hidden text-gray-900 dark:text-gray-100'
        size={24}
        onClick={() => {
          setIsMobileMenuOpen(!isMobileMenuOpen);
        }}
      />
      <ul
        id='mobile-menu'
        className={cn(
          'block md:hidden absolute top-12 p-4 right-0 bg-gray-200 dark:bg-gray-800 w-screen text-[1.5em] leading-[1.5em] shadow-lg',
          isMobileMenuOpen ? 'block' : 'hidden'
        )}
      >
        {availableNavItems
          .filter((item) => item.public || isSignedIn)
          .map((item) => {
            const adminTextColor =
              item.id === 'admin'
                ? 'text-[#e9e9e9]'
                : 'text-emerald-700 dark:text-emerald-300';
            return (
              <li key={item.id} className='w-full'>
                <a
                  href={item.href}
                  onMouseEnter={() => handleNavMouseEnter(item)}
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                  }}
                  className={`hover:underline w-full flex justify-start gap-4 items-center btn btn-link text-lg ${adminTextColor}`}
                >
                  {item.icon}
                  <span className='text-[1.3em]'>{item.label}</span>
                </a>
              </li>
            );
          })}
        <li className='w-full flex justify-start'>
          {isSignedIn ? (
            <button
              className='flex gap-2 items-center btn btn-link mx-0 text-emerald-700 dark:text-emerald-300'
              onClick={handleSignOut}
            >
              <Image
                src={user.imageUrl}
                className='avatar rounded-full'
                alt='User'
                width={28}
                height={28}
                style={{
                  maxWidth: '100%',
                  height: 'auto',
                }}
              />
              Sign Out
            </button>
          ) : (
            <SignInButton mode='modal'>
              <Link
                href='/user/sign-in'
                className='btn btn-link flex gap-4 items-center text-emerald-700 dark:text-emerald-300'
              >
                <FaUser className='icon' size={24} />
                <span>Sign In</span>
              </Link>
            </SignInButton>
          )}
        </li>
      </ul>
      <div className='flex w-full items-center justify-between gap-2'>
        <div className='flex items-center gap-1 min-w-0'>
          <Link
            href='/'
            className='flex items-center gap-1'
            aria-label='TrainMe dashboard'
          >
            <div
              className='bg-slate-200 dark:bg-slate-300 rounded-full w-8 h-8'
              title={gitCommit}
            >
              <Image
                src='/trainme.webp'
                alt='Logo'
                width={48}
                height={48}
                style={{
                  maxWidth: '100%',
                  height: 'auto',
                }}
              />
            </div>
            <h1 className='hidden xs:block text-emerald-700 h1 mx-0'>
              TrainMe
            </h1>
          </Link>
          {showCalendarTodayButton && visibleCalendarMonth && (
            <div ref={datePickerRef} className='relative ml-2 xs:ml-8 min-w-0'>
              <button
                type='button'
                onClick={() => setIsDatePickerOpen((open) => !open)}
                className='inline-flex items-center gap-1 xs:gap-3 max-w-[6.5rem] xs:max-w-none text-sm xs:text-base font-semibold uppercase tracking-wide text-slate-700 dark:text-slate-200'
                aria-expanded={isDatePickerOpen}
                aria-haspopup='dialog'
              >
                <span className='truncate'>{visibleCalendarMonth}</span>
                <FaChevronRight
                  className={cn(
                    'shrink-0 text-xs transition-transform',
                    isDatePickerOpen && 'rotate-90'
                  )}
                />
              </button>
              {isDatePickerOpen && (
                <div className='calendar-date-picker absolute left-0 top-full z-30 mt-4 rounded-xl bg-white p-4 text-slate-900 shadow-2xl dark:bg-slate-800 dark:text-slate-100'>
                  <Calendar
                    activeStartDate={startOfMonth(datePickerMonth)}
                    onActiveStartDateChange={({ activeStartDate }) => {
                      if (activeStartDate) setDatePickerMonth(activeStartDate);
                    }}
                    onChange={(value) => {
                      if (value instanceof Date) {
                        handleCalendarDateSelect(value);
                      }
                    }}
                    value={datePickerMonth}
                    showNeighboringMonth
                    prev2Label={null}
                    next2Label={null}
                  />
                </div>
              )}
            </div>
          )}
          {showCalendarTodayButton && (
            <div className='ml-1 xs:ml-2 inline-flex items-center text-emerald-800 dark:text-emerald-200'>
              <button
                type='button'
                onClick={() => handleCalendarNavigate('up')}
                className='hidden xs:block rounded-l-full p-2 transition hover:text-emerald-600 dark:hover:text-emerald-100'
                aria-label='Scroll calendar up'
                title='Scroll calendar up'
              >
                <FaChevronLeft className='text-xs' />
              </button>
              <button
                type='button'
                onClick={handleTodayClick}
                className='px-2 py-2 xs:px-4 text-sm xs:text-base font-semibold uppercase tracking-wide transition hover:text-emerald-600 dark:hover:text-emerald-100'
                aria-label='Scroll to today'
                title='Scroll to today'
              >
                Today
              </button>
              <button
                type='button'
                onClick={() => handleCalendarNavigate('down')}
                className='hidden xs:block rounded-r-full p-2 transition hover:text-emerald-600 dark:hover:text-emerald-100'
                aria-label='Scroll calendar down'
                title='Scroll calendar down'
              >
                <FaChevronRight className='text-xs' />
              </button>
            </div>
          )}
        </div>
        <div className='flex justify-end items-center gap-2'>
          <nav className='flex justify-end items-center text-emerald-700 dark:text-emerald-300 gap-2'>
            {isSignedIn && <CoachButton />}
            {isSignedIn && (
              <ul
                id='desktop-menu'
                className='hidden md:flex gap-2 justify-between items-center text-emerald-700 dark:text-emerald-300'
              >
                {availableNavItems
                  .filter((item) => item.public || isSignedIn)
                  .map((item) => {
                    const adminTextColor =
                      item.id === 'admin'
                        ? 'text-[#e9e9e9]'
                        : 'text-emerald-700 dark:text-emerald-300';
                    return (
                      <li key={item.id}>
                        <a
                          href={item.href}
                          onMouseEnter={() => handleNavMouseEnter(item)}
                          onClick={item.onClick}
                          className={`hover:underline flex gap-2 items-center btn btn-link text-lg ${adminTextColor}`}
                        >
                          {item.icon}
                          <span className='text-lg'>{item.label}</span>
                        </a>
                      </li>
                    );
                  })}
              </ul>
            )}
            {isSignedIn ? (
              <UserButton
                appearance={{
                  elements: {
                    avatarBox: 'h-8 w-8',
                  },
                }}
              >
                <UserButton.UserProfilePage
                  label='Danger Zone'
                  labelIcon={<FaTriangleExclamation className='text-red-600' />}
                  url='danger-zone'
                >
                  <div className='space-y-4'>
                    <h1 className='text-xl font-semibold'>Danger Zone</h1>
                    <DeleteAccount />
                  </div>
                </UserButton.UserProfilePage>
              </UserButton>
            ) : (
              <SignInButton mode='redirect'>
                <button className='btn btn-link text-emerald-700 dark:text-emerald-300'>
                  <FaUser className='icon' />
                  <span className='text-[0.9em]'>Sign In/Up</span>
                </button>
              </SignInButton>
            )}
          </nav>
        </div>
      </div>
      <ToastContainer
        theme={isDarkMode ? 'dark' : 'light'}
        autoClose={7 * 1000}
        position='bottom-left'
        hideProgressBar={false}
        closeOnClick={false}
        pauseOnHover={false}
        draggable={true}
        pauseOnFocusLoss={false}
        className='toast-container'
        key={isDarkMode ? 'dark' : 'light'}
      />
      {isSignedIn && <ChatModal />}
    </header>
  );
};

export default Header;
